import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as pbi from 'powerbi-client';
import {
  IEmbedConfiguration,
  IEmbedSettings,
  models,
  Report,
  service,
  Visual,
} from 'powerbi-client';
import {
  Observable,
  Subject,
  Subscription,
  distinctUntilChanged,
  from,
  map,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { EmbeddedTokenResponse } from 'src/app/shared/interfaces/embedded-token-model';
import { PowerbiReportService } from 'src/app/shared/services/powerbi-report.service';
import { LoaderService } from '../../services/loader.service';

@Component({
  selector: 'app-powerbi-template',
  templateUrl: './powerbi-template.component.html',
  styleUrl: './powerbi-template.component.scss',
  standalone: true,
})
export class PowerbiTemplateComponent implements OnInit, AfterViewInit, OnDestroy {
  type = '';

  @Input() embeddedVisualToken: Observable<EmbeddedTokenResponse> | undefined;
  @Input() pageName: string | undefined;
  @Input() visualName: string | undefined;

  @Output() onPagesInitialized = new EventEmitter<pbi.Page[]>();

  @ViewChild('embedContainer', { static: true }) embedContainer:
    | ElementRef<HTMLDivElement>
    | undefined;

  isLoading = false;
  HasError = false;

  private readonly destroy$: Subject<void> = new Subject();
  private readonly powerbi: service.Service;
  private report: Report | undefined;
  private readonly reportConfig: IEmbedConfiguration | undefined;

  private readonly hasReportBeenLoaded = false;
  private readonly _dispatchToken = new Subject<EmbeddedTokenResponse>();
  private readonly _dispatchError = new Subject<boolean>();
  embeddedReportToken: Observable<EmbeddedTokenResponse> | undefined;
  onError: Observable<boolean> | undefined;
  tokenSubscription?: Subscription;

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly powerbiReportService: PowerbiReportService,
    private readonly route: ActivatedRoute,
    private readonly loader: LoaderService,
  ) {
    this.powerbi = new service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory,
    );

    this.route.data
      .pipe(
        takeUntil(this.destroy$),
        tap(data => {
          this.type = data['type'];
        }),
      )
      .subscribe();

    if (this.type === undefined || this.type === '') {
      this.type = localStorage.getItem('cockpitType') || '';
    }
  }

  ngOnInit() {
    this.loader.setLoading(true);
    this.powerbiReportService
      .generateToken(this.type)
      .pipe(take(1))
      .subscribe({
        next: token => {
          this._dispatchToken.next(token);
          this.initialiseReport(token);
        },
        error: _ => this._dispatchError.next(true),
      });

    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(
        takeUntil(this.destroy$),
        map(breakpointState => breakpointState.matches),
        distinctUntilChanged(),
      )
      .subscribe(isMobile => {
        let settings: IEmbedSettings = {
          filterPaneEnabled: false,
          navContentPaneEnabled: false,
          layoutType: isMobile ? models.LayoutType.MobilePortrait : undefined,
          panes: {
            filters: { visible: false },
            pageNavigation: { visible: false },
          },
        };

        this.isLoading = true;
        this.embedContainer!.nativeElement.style.visibility = 'hidden';

        if (this.hasReportBeenLoaded) {
          this.enforceReload(settings);
        }
      });

    if (this.onError) {
      this.onError.pipe(takeUntil(this.destroy$)).subscribe(isError => {
        if (isError) {
          this.embedContainer!.nativeElement.style.visibility = 'hidden';
          this.isLoading = false;
          this.HasError = true;
        }
      });
    }

    if (this.embeddedVisualToken) {
      this.embeddedVisualToken
        .pipe(takeUntil(this.destroy$))
        .subscribe(token => this.intialiseReportVisual(token));
    }
  }

  ngAfterViewInit() {
    this.changeStyle();
  }

  ngOnDestroy() {
    if (this.embedContainer) {
      this.powerbi.reset(this.embedContainer.nativeElement);
    }

    if (this.tokenSubscription) {
      this.tokenSubscription.unsubscribe();
    }
    this.destroy$.next();
  }

  /**
   * Dynamically applies styles from the embedded Power BI report's internal display area
   * to the iframe element containing the report.
   *
   * This is primarily used to sync visual styles (like borders) between the iframe container
   * and the content inside it, enhancing visual consistency.
   *
   * ⚠️ Note: This relies on access to the iframe's internal document. If the iframe content
   * is cross-origin, this will fail silently due to browser security restrictions (CORS).
   *
   * Example use case: align the border of the host iframe with the report's `.displayArea` style.
   */
  private changeStyle() {
    const iframe = document.querySelector<HTMLIFrameElement>('embedContainer'); // sélectionnez votre iFrame ici

    if (iframe) {
      const iframeContent = iframe.contentWindow?.document;
      const displayArea = iframeContent?.querySelector('.displayArea');

      if (displayArea) {
        const displayAreaStyles = window.getComputedStyle(displayArea);
        iframe.style.border = displayAreaStyles.border;
      }
    }
  }

  /**
   * Forces a full reload of the embedded Power BI report with updated settings.
   *
   * This method is useful when layout or pane visibility settings need to be
   * dynamically changed (e.g., adapting to screen size). It resets the existing
   * report embed, applies the new settings to the configuration, and re-embeds
   * the report in the container.
   *
   * @param {IEmbedSettings} settings - The updated embed settings to apply (e.g., layout, pane visibility).
   */
  private enforceReload(settings: IEmbedSettings) {
    settings.filterPaneEnabled = false;
    settings.navContentPaneEnabled = false;
    settings.panes = {
      filters: { visible: false },
      pageNavigation: { visible: false },
    };

    this.reportConfig!.settings = settings;
    this.powerbi.reset(this.embedContainer!.nativeElement);

    this.report = <Report>this.powerbi.embed(this.embedContainer!.nativeElement, this.reportConfig);
    this.isLoading = false;
    this.report.on(
      'loaded',
      () => (this.embedContainer!.nativeElement.style.visibility = 'visible'),
    );
  }

  /**
   * Initializes and embeds a full Power BI report into the container element.
   *
   * This method checks if an embed configuration is cached for the given token.
   * If not, it creates a new configuration and caches it. Then it embeds the
   * report using the Power BI JavaScript SDK.
   *
   * Once the report is loaded, it emits the list of pages via the
   * `onPagesInitialized` event emitter and updates the loading state.
   *
   * @param {EmbeddedTokenResponse} token - The token response containing access and embed information for the report.
   */
  private initialiseReport(token: EmbeddedTokenResponse) {
    const container = this.embedContainer!.nativeElement;
    const tokenKey = token.EmbedToken.Token;

    this.resetIfNeeded(container);

    let config = this.powerbiReportService.getCachedConfig(tokenKey);
    if (!config) {
      config = {
        type: 'report',
        tokenType: models.TokenType.Embed,
        accessToken: token.EmbedToken.Token,
        embedUrl: token.EmbedReport[0].EmbedUrl,
        id: token.EmbedReport[0].ReportId,
        permissions: models.Permissions.All,
        viewMode: models.ViewMode.View,
        settings: {
          filterPaneEnabled: false,
          navContentPaneEnabled: false,
          panes: {
            filters: { visible: false },
            pageNavigation: { visible: false },
          },
        },
      };
      this.powerbiReportService.cacheConfig(tokenKey, config);
    }

    this.report = <Report>this.powerbi.embed(container, config);

    this.report.on('loaded', () => {
      this.loader.setLoading(false);
      this.onReportInitialized(this.report!);
      this.isLoading = false;
      container.style.visibility = 'visible';

      from(this.report!.getPages())
        .pipe(
          takeUntil(this.destroy$),
          tap((pages: any) => this.onPagesInitialized.emit(pages)),
        )
        .subscribe();
    });
  }

  /**
   * Initializes and embeds a specific Power BI visual into the container.
   *
   * This method uses an embed configuration of type 'visual' to load a single
   * visual from a Power BI report, identified by its report ID, page name,
   * and visual name. It ensures the visual is embedded into the container and
   * sets up event listeners for lifecycle events (loaded, rendered, error).
   *
   * @param {EmbeddedTokenResponse} token - The token response containing embed information and access token.
   */
  private intialiseReportVisual(token: EmbeddedTokenResponse) {
    // Report Config
    let config = {
      type: 'visual',
      tokenType: models.TokenType.Embed,
      accessToken: token.EmbedToken.Token,
      embedUrl: token.EmbedReport[0].EmbedUrl,
      id: token.EmbedReport[0].ReportId,

      pageName: this.pageName,
      visualName: this.visualName,
    };

    const visual = <Visual>this.powerbi.embed(this.embedContainer!.nativeElement, config);
    this.onReportInitialized(visual);

    // visual.off removes all event handlers for a specific event
    visual.off('loaded');

    // visual.on will add an event handler
    visual.on('loaded', function () {
      // Event handler code
    });

    // visual.off removes all event handlers for a specific event
    visual.off('rendered');

    // visual.on will add an event handler
    visual.on('rendered', function () {
      // Event handler code
    });

    // visual.off removes all event handlers for a specific event
    visual.off('error');
  }

  /**
   * Called when a Power BI report is initialized.
   * @param {pbi.Report} report - The Power BI report object.
   */
  onReportInitialized(report: pbi.Report) {
    const startingPage: string = 'acceuil';
    if (startingPage) {
      this.showPage(report, startingPage);
    }
  }

  /**
   * Shows a specific page on the given Power BI report.
   * @param {pbi.Report} report - The Power BI report object.
   * @param {string} displayName - The display name of the page to show.
   */
  private showPage(report: pbi.Report, displayName: string) {
    report.getPages().then(pages => {
      const page = pages.find(page => page.displayName.trim() === displayName.trim());
      report.setPage(page?.name ?? '');
    });
  }

  /**
   * Clean the embed container if a report is already present.
   * Ensures we can safely call powerbi.embed() again.
   */
  private resetIfNeeded(container: HTMLElement) {
    try {
      const existingEmbed = this.powerbi.get(container);
      if (existingEmbed) {
        this.powerbi.reset(container);
      }
    } catch (e) {
      console.error('Error resetting Power BI embed:', e);
    }

    container.innerHTML = '';
  }
}
